import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { useCallback } from 'react';
import {
    downloadInvoicePdf,
    downloadInvoicesPdfZip,
    exportCsv,
    getCsvForDraftInvoice,
    getPdfForDraftInvoice,
} from 'store/invoices/client';
import { DocumentResponse, UpdateInvoicePayload } from 'store/invoices/types';
import { extractFileNameFromPreSigneUrl, triggerBrowserDownload } from 'utils/download-utils';
import { getErrorMessage } from 'utils/getErrorMessage';

type ExportResult = { blob: Blob; fileName?: string };

type RunExportOptions = {
    loadingMessage: string;
    successTitle: string;
    successDescription: string;
    fallbackFileName: string;
    getExportResult: () => Promise<ExportResult>;
    rethrow?: boolean;
    onError?: (err: unknown) => void | Promise<void>;
};

export type ExportInvoiceParams =
    | { type: 'csv'; invoiceId: string; orderNumber: string }
    | { type: 'pdf'; invoiceId: string; orderNumber: string }
    | { type: 'bulk-csv'; invoiceIds: string[] }
    | { type: 'bulk-pdf-zip'; invoiceIds: string[] }
    | {
          type: 'draft-pdf';
          invoiceId: string;
          orderNumber: string;
          payload: UpdateInvoicePayload;
          onError?: (err: unknown) => void | Promise<void>;
      }
    | {
          type: 'draft-csv';
          invoiceId: string;
          orderNumber: string;
          payload: UpdateInvoicePayload;
          onError?: (err: unknown) => void | Promise<void>;
      };

export const useExportInvoice = () => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const runExport = useCallback(
        async ({
            loadingMessage,
            successTitle,
            successDescription,
            fallbackFileName,
            getExportResult,
            rethrow,
            onError,
        }: RunExportOptions) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: loadingMessage } });

                const { blob, fileName } = await getExportResult();
                triggerBrowserDownload(blob, fileName ?? fallbackFileName);

                Notification({
                    type: 'success',
                    placement: 'top',
                    messageTitle: successTitle,
                    description: successDescription,
                });
            } catch (err) {
                if (onError) {
                    await onError(err);
                } else {
                    const errorMessage = getErrorMessage(err);
                    Notification({
                        type: 'error',
                        placement: 'top',
                        messageTitle: 'Operation Failed',
                        description: errorMessage || DEFAULT_INTERNAL_ERROR_MESSAGE,
                    });
                }

                if (rethrow) throw err;
            } finally {
                hideGlobalOverlay();
            }
        },
        [showGlobalOverlay, hideGlobalOverlay]
    );

    const downloadFromPreSignedUrl = useCallback(async (documentResponse: DocumentResponse): Promise<ExportResult> => {
        const response = await fetch(documentResponse.preSignedUrl);

        if (!response.ok) {
            throw new Error('Could not get file from the server. Please try again later.');
        }

        const blob = await response.blob();
        const fileName = extractFileNameFromPreSigneUrl(documentResponse.preSignedUrl);

        return { blob, fileName };
    }, []);

    const exportInvoice = useCallback(
        (params: ExportInvoiceParams) => {
            switch (params.type) {
                case 'csv':
                    return runExport({
                        loadingMessage: 'Downloading CSV',
                        successTitle: 'CSV Downloaded',
                        successDescription: 'The CSV for the invoice downloaded successfully.',
                        fallbackFileName: params.orderNumber,
                        getExportResult: async () => downloadFromPreSignedUrl(await exportCsv([params.invoiceId])),
                    });

                case 'pdf':
                    return runExport({
                        loadingMessage: 'Downloading invoice PDF',
                        successTitle: 'PDF Downloaded',
                        successDescription: 'The PDF for the invoice downloaded successfully.',
                        fallbackFileName: params.orderNumber,
                        getExportResult: async () =>
                            downloadFromPreSignedUrl(await downloadInvoicePdf(params.invoiceId)),
                        rethrow: true,
                    });

                case 'bulk-csv':
                    return runExport({
                        loadingMessage: 'Exporting invoices CSV',
                        successTitle: 'Invoices Downloaded',
                        successDescription: 'The invoices have been downloaded successfully.',
                        fallbackFileName: 'InvoicesCSV',
                        getExportResult: async () => downloadFromPreSignedUrl(await exportCsv(params.invoiceIds)),
                    });

                case 'bulk-pdf-zip':
                    return runExport({
                        loadingMessage: 'Exporting PDF zip',
                        successTitle: 'Invoices Downloaded',
                        successDescription: 'The invoices have been downloaded successfully.',
                        fallbackFileName: 'InvoicesZIP',
                        getExportResult: async () =>
                            downloadFromPreSignedUrl(await downloadInvoicesPdfZip(params.invoiceIds)),
                    });

                case 'draft-pdf':
                    return runExport({
                        loadingMessage: 'Generating PDF',
                        successTitle: 'Invoice PDF Downloaded',
                        successDescription: 'The invoice PDF have been successfully downloaded.',
                        fallbackFileName: `Draft-${params.orderNumber}.pdf`,
                        getExportResult: async () => ({
                            blob: await getPdfForDraftInvoice(params.invoiceId, params.payload),
                        }),
                        onError: params.onError,
                    });

                case 'draft-csv':
                    return runExport({
                        loadingMessage: 'Downloading CSV',
                        successTitle: 'Invoice CSV downloaded',
                        successDescription: 'The invoice CSV have been successfully downloaded.',
                        fallbackFileName: `Draft-${params.orderNumber}.csv`,
                        getExportResult: async () => ({
                            blob: await getCsvForDraftInvoice(params.invoiceId, params.payload),
                        }),
                        onError: params.onError,
                    });
            }
        },
        [runExport, downloadFromPreSignedUrl]
    );

    return { exportInvoice };
};
